import {useState, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {AppDispatch} from '../redux/store';
import {OrderTypeValueType} from '../types/TypesStore';
import {
	depotOrdersApi,
	useGetProductsListQuery,
	useLazyEditOrderQuery,
} from '../redux/DepotSlice/depotOrdersApi';
import {useTranslation} from 'react-i18next';
import {useProductsHandler} from './useProductsHandler';
import {useBarcodeScanner} from './useBarcodeScanner';
import {dateFormatter} from '../assets/utils/dateUtils';
import {useLazyEditActivitiesQuery} from '../redux/activitiesApi';
import {ResponseEditOrderType as OrderResponseType} from '../types/TypesStore';
import {ResponseEditOrderType as ActivitiesResponseType} from '../types/TypesDepotPaperwork';
import {toast} from 'react-toastify';

export type {SelectedProductType} from './useProductsHandler';

type ContextType = 'order' | 'activities';

export const useCreateInvoiceDesignForm = (
	initOrderType: OrderTypeValueType,
	id?: string,
	context: ContextType = 'order',
	clientId?: number,
	clientType?: 'loyal' | 'ordinary',
) => {
	const dispatch: AppDispatch = useDispatch();
	const date = new Date();
	const formatedDate = dateFormatter(date);
	const {t} = useTranslation();
	const [createPopup, setCreatePopup] = useState(false);
	const [orderType, setOrderType] = useState<OrderTypeValueType>(initOrderType);
	const [isRetail, setIsRetail] = useState<boolean>(clientType === 'ordinary'); // false = опт (loyal), true = роздріб (ordinary)
	const [categoryId, setCategoryId] = useState<number>();
	const [valueDate, setValueDate] = useState<string>(formatedDate);
	const [isInitialized, setIsInitialized] = useState(false);

	const [getOrderInfo, {data: editOrderInfo}] = useLazyEditOrderQuery();
	const [getActivitiesInfo, {data: editActivityInfo}] =
		useLazyEditActivitiesQuery();

	// Выбираем данные в зависимости от контекста
	const data: OrderResponseType | ActivitiesResponseType | undefined =
		context === 'order' ? editOrderInfo : editActivityInfo;
	const getDataInfo = context === 'order' ? getOrderInfo : getActivitiesInfo;

	// Вспомогательные функции для безопасного доступа к данным
	const isOrderData = (data: any): data is OrderResponseType =>
		context === 'order' && data && 'type' in data;

	const isActivitiesData = (data: any): data is ActivitiesResponseType =>
		context === 'activities' && data && 'order_date' in data;
	
	// Обновляем isRetail при изменении типа клиента
	useEffect(() => {
		if (clientType) {
			setIsRetail(clientType === 'ordinary');
		}
	}, [clientType]);
	
	const {data: productsList, isLoading: isLoadingProductList} =
		useGetProductsListQuery({
			type_order: orderType,
			order_date: valueDate,
			...(categoryId ? {category_id: categoryId.toString()} : {}),
			retail: isRetail, // true для роздрібу, false для опту
			...(clientId ? {client_id: clientId} : {}),
		});

	const {
		selectedProducts,
		setSelectedProducts,
		filterByStock,
		searchByName,
		productsForRender,
		handleChangeProduct,
		handleChangeSearch,
		handleClickButtonInPanel,
		setSearchByName,
	} = useProductsHandler(productsList);

	// Хук для сканирования баркодов
	const {
		isScanning,
		lastScannedBarcode,
		scannedProducts,
		isCameraActive,
		cameraPermission,
		showManualInput,
		inputRef: barcodeInputRef,
		videoRef: barcodeVideoRef,
		handleInputFocus: handleBarcodeInputFocus,
		handleInputBlur: handleBarcodeInputBlur,
		handleInputChange: handleBarcodeInputChange,
		addProductByBarcode,
		removeScannedProduct,
		clearScannedProducts,
		findProductByBarcode,
		startCameraScanning,
		stopCameraScanning,
		checkCameraPermission,
		setIsScanning: setBarcodeScanning,
		setShowManualInput,
	} = useBarcodeScanner(productsList, {
		onProductFound: (product) => {
			// Автоматически добавляем найденный товар в выбранные
			handleChangeProduct(product.id, '+');
			// Показываем уведомление об успешном добавлении
			toast.success(t(`Товар "${product.name}" додано до списку!`), {
				autoClose: 2000,
				position: 'top-right'
			});
		},
		onProductNotFound: (barcode) => {
			toast.warning(t(`Товар з баркодом ${barcode} не знайдено`), {
				autoClose: 3000,
				position: 'top-right'
			});
		},
		onError: (error) => {
			toast.error(t('Помилка сканування баркоду'), {
				autoClose: 3000,
				position: 'top-right'
			});
		},
		debounceTime: 500,
		minBarcodeLength: 5,
		enableCamera: true, // Включаем поддержку камеры
		alwaysListen: true, // Всегда слушаем баркоды
		cameraConfig: {
			width: 640,
			height: 480,
			facingMode: 'environment' // Задняя камера для лучшего сканирования
		}
	});

	// Управление попапом
	const closePopup = () => setCreatePopup(false);
	const handleClickReadyButton = () => setCreatePopup(true);

	useEffect(() => {
		if (id) {
			getDataInfo(+id);
		}
	}, [id, getDataInfo]);

	useEffect(() => {
		dispatch(
			depotOrdersApi.util.updateQueryData(
				'getProductsList',
				{
					type_order: orderType,
					order_date: valueDate,
					...(categoryId ? {category_id: categoryId.toString()} : {}),
					retail: isRetail, // true для роздрібу, false для опту
					...(clientId ? {client_id: clientId} : {}),
				},
				(draft) => {
					draft.products?.forEach((product) => {
						const selected = selectedProducts.find((p) => p.id === product.id);
						const buyQuantity = selected?.buyQuantity ?? 0;
						product.buyQuantity = buyQuantity || undefined;
					});
				},
			),
		);
	}, [selectedProducts, categoryId, orderType, dispatch, valueDate, isRetail, clientId]);

	useEffect(() => {
		if (data && id && !isInitialized && productsList?.products) {
			// Создаем товары из данных заказа
			const productsToAdd = data.products.map((orderProduct) => {
				// Ищем товар в текущем списке товаров
				const foundProduct = productsList.products?.find(
					(p) => Number(p.id) === orderProduct.product_id,
				);

				if (foundProduct) {
					// Если товар найден в списке, используем его данные с количеством из заказа
					return {
						...foundProduct,
						buyQuantity: orderProduct.count ?? 0,
						// Обновляем count_store, вычитая количество из заказа
						count_store: Math.max(
							0,
							Number(foundProduct.count_store) - (orderProduct.count ?? 0),
						),
					};
				} else {
					// Если товар не найден в списке, создаем базовый объект с достаточным количеством для редактирования
					return {
						id: orderProduct.product_id.toString(),
						name: `Товар ${orderProduct.product_id}`,
						href: '',
						count_store: orderProduct.count ?? 0, // Используем количество из заказа как доступное
						points: '0',
						money: '0',
						sale: '0',
						in_stock: true,
						comment: '',
						buyQuantity: orderProduct.count ?? 0,
						cashback: '0',
						barcode: '',
						weight: '0',
						visible_count: true,
					};
				}
			});

			setSelectedProducts(productsToAdd);
			setIsInitialized(true);
		}
	}, [data, id, isInitialized, productsList?.products, setSelectedProducts]);

	const functionalityButtons = useMemo(
		() => [
			{name: t('Показати всі'), value: 'all'},
			{
				name: t('В наявності'),
				value: 'inStock',
				active: filterByStock === true,
			},
			{
				name: t('Обрані'),
				value: 'selected',
				active: filterByStock === false,
			},
		],
		[t, filterByStock],
	);

	const productsForRenderMemo = useMemo(
		() => productsForRender,
		[productsList?.products, selectedProducts, filterByStock, searchByName],
	);

	return {
		orderType,
		setOrderType,
		isRetail,
		setIsRetail,
		categoryId,
		setCategoryId,
		searchByName,
		selectedProducts,
		setSelectedProducts,
		createPopup,
		isLoadingProductList,
		productsForRender: productsForRenderMemo,
		functionalityButtons,
		data,
		context,
		isOrderData,
		isActivitiesData,
		handleClickButtonInPanel,
		handleChangeProduct,
		handleChangeSearch,
		handleClickReadyButton,
		closePopup,
		valueDate,
		setSearchByName,
		setValueDate,
		// Функциональность сканирования баркодов
		isScanning,
		lastScannedBarcode,
		scannedProducts,
		isCameraActive,
		cameraPermission,
		showManualInput,
		barcodeInputRef,
		barcodeVideoRef,
		handleBarcodeInputFocus,
		handleBarcodeInputBlur,
		handleBarcodeInputChange,
		addProductByBarcode,
		removeScannedProduct,
		clearScannedProducts,
		findProductByBarcode,
		startCameraScanning,
		stopCameraScanning,
		checkCameraPermission,
		setBarcodeScanning,
		setShowManualInput,
	};
};
